import { detectBlocks, type DetectedBlocksResult } from "./detectBlocks";
import { buildFollowUpPlan } from "./buildFollowUpPlan";
import { buildGamePlan } from "./buildGamePlan";
import { inferSkillsFromBlocks } from "./inferSkillsFromBlocks";
import {
  isLogisticalAvailabilityStatement,
  stripLogisticalAvailabilityStatements,
} from "./schedulingContext";
import {
  classifySafetyReference,
  detectCumulativeSafetyAdjacentSignal,
  getSafetyContextState,
  getSafetyContextWindow,
} from "./safetyContext";
import { inferCurrentStep, type NeurogresStepInference } from "./stepRouter";
import type { NeurogresGame, NeurogresSkill } from "./skillGameMap";

const operationalIntent = require("./operationalIntent");
const {
  maskQuotedParticipantSpeech,
} = require("./participantEvidence");
const {
  classifyFactualPlanningIntent,
  classifyOperationalIntent,
  isUnambiguousExplicitSessionClose,
} = operationalIntent;

type MessageLike = {
  role?: string;
  sender?: string;
  content?: string;
  text?: string;
};

export interface CoachingStateInput {
  latestUserMessage?: string | null;
  recentAssistantMessages?: string[] | null;
  messages?: MessageLike[] | null;
  sessionSummary?: Record<string, unknown> | null;
}

export interface CoachingState {
  responseRoute: "inform" | "advise" | "coach" | "support";
  currentStep: NeurogresStepInference;
  promptPriority: "force_obstacle_first" | "obstacle_first" | "predicted_outcome" | "default";
  coachingMode: "explore" | "direct" | "challenge" | "stabilise";
  safetyEscalation?: {
    level: "none" | "deescalated" | "ambiguous_safety" | "ambiguous_safety_reference" | "first_person_historical" | "third_person_current" | "third_person_historical" | "quoted_or_informational" | "cumulative_safety_adjacent" | "passive_risk" | "direct_danger" | "real_help";
    safetySignal: boolean;
    ambiguousSafetySignal: boolean;
    cumulativeSafetyAdjacentSignal?: boolean;
    realHelpSignal: boolean;
    urgent: boolean;
    reviewFlagRecommended: boolean;
    matched: string;
    safetyContextClassification?: string;
    otherPersonSafetySignal?: boolean;
    historicalSafetySignal?: boolean;
    explicitSafetyDeescalation?: boolean;
    quotedOrInformationalSafetyReference?: boolean;
  };
  forceChallenge: boolean;
  presentingIssue: string;
  currentStory: string;
  repeatPhrases: string[];
  obstacleFirst: boolean;
  forceObstacleFirst: boolean;
  persistentObstacle: boolean;
  symptomLikeReply: boolean;
  predictedOutcomeStory: boolean;
  predictedOutcomeQuestion: string;
  obstacleQuestion: string;
  primaryStoppingQuestion: string;
  primaryBlock: string;
  internalObstacles: string[];
  externalObstacles: string[];
  limitingBeliefs: string[];
  distortionPatterns: string[];
  targetSkills: NeurogresSkill[];
  recommendedGames: NeurogresGame[];
  gameRationales: string[];
  futureState: string;
  immediateTarget: string;
  actions: string[];
  timelines: string[];
  supportNeeds: string[];
  nextFollowUpFocus: string;
  sessionComplete: boolean;
  completionReason: string;
  completionAction: string;
  debugStep?: string;
  debugSkills?: string[];
  debugGames?: string[];
  operationalIntent?: {
    operational: boolean;
    complaint: boolean;
    escalationRequest: boolean;
    missingSummary: boolean;
    continuityRequest: boolean;
    recordRequest: boolean;
    reminderComplaint: boolean;
    deliveryComplaint: boolean;
    inaccurateRecap: boolean;
    establishedContext: boolean;
    pureOperational: boolean;
    hasCoachingTopic: boolean;
    residualText: string;
    reason: string;
  };
  factualPlanning?: {
    factualPlanning: boolean;
    retirementPlanning: boolean;
  };
}

function normalizeText(value = ""): string {
  return String(value || "").replace(/[’‘]/g, "'").replace(/\s+/g, " ").trim();
}

function detectBoundedHoldingSafetySignal(messages: MessageLike[] = [], latestText = "") {
  const recentUserTexts = messages
    .filter((entry) => String(entry?.role || entry?.sender || "").toLowerCase() === "user")
    .map((entry) => normalizeText(entry?.content || entry?.text || ""))
    .filter(Boolean)
    .slice(-6);
  const latest = normalizeText(latestText || recentUserTexts[recentUserTexts.length - 1] || "").toLowerCase();
  const joined = [...recentUserTexts, latest].join(" ").toLowerCase();
  if (!latest && !joined) return null;

  const immediateMatch = [
    /\bi(?:'| a)?m barely holding on\b/i,
    /\bi do not know how much longer i can keep doing this\b/i,
    /\bi don'?t know how much longer i can keep doing this\b/i,
    /\bi might not recover\b/i,
    /\bnothing left\b/i,
    /\bno hope\b/i,
  ].map((pattern) => latest.match(pattern)?.[0]).find(Boolean);
  if (immediateMatch) {
    return {
      level: "passive_risk" as const,
      safetySignal: true,
      ambiguousSafetySignal: false,
      realHelpSignal: false,
      urgent: true,
      reviewFlagRecommended: true,
      matched: normalizeText(immediateMatch),
    };
  }

  const holdingRequests = recentUserTexts.filter((text) =>
    /\b(no advice|don'?t give advice|do not give advice|no moving forward|don'?t move forward|do not move forward|just stay|stay with (?:this|the)|only reflect|hold (?:this|the pain))\b/i.test(text)
  ).length;
  const distressMatches = [
    /\bbarely holding on\b/i,
    /\bdon'?t know how much longer\b/i,
    /\bcan'?t keep going\b/i,
    /\bcannot keep going\b/i,
    /\bunbearable\b/i,
    /\bcomplete collapse\b/i,
    /\bif i stop[,]? i'?ll fall apart\b/i,
    /\bfall apart\b/i,
    /\bno hope\b/i,
    /\bcompletely empty\b/i,
    /\bmight not recover\b/i,
    /\bnothing left\b/i,
  ].reduce((count, pattern) => count + (pattern.test(joined) ? 1 : 0), 0);
  if (holdingRequests >= 2 && distressMatches >= 2) {
    return {
      level: "passive_risk" as const,
      safetySignal: true,
      ambiguousSafetySignal: false,
      realHelpSignal: false,
      urgent: true,
      reviewFlagRecommended: true,
      matched: "bounded_holding_distress_accumulation",
    };
  }
  return null;
}

function detectSafetyEscalationSignal(text = "", messages: MessageLike[] = []) {
  const normalized = normalizeText(text).toLowerCase();
  if (!normalized) {
    return {
      level: "none" as const,
      safetySignal: false,
      ambiguousSafetySignal: false,
      realHelpSignal: false,
      urgent: false,
      reviewFlagRecommended: false,
      matched: "",
    };
  }

  const contextualReference = classifySafetyReference(normalized);
  if (contextualReference.classification === "explicit_deescalation") {
    return {
      level: "deescalated" as const,
      safetySignal: false,
      ambiguousSafetySignal: false,
      realHelpSignal: false,
      urgent: false,
      reviewFlagRecommended: false,
      safetyContextClassification: contextualReference.classification,
      explicitSafetyDeescalation: true,
      matched: "explicit_personal_safety_clarification",
    };
  }
  if (contextualReference.classification === "first_person_current") {
    return {
      level: "direct_danger" as const,
      safetySignal: true,
      ambiguousSafetySignal: false,
      realHelpSignal: false,
      urgent: true,
      reviewFlagRecommended: true,
      safetyContextClassification: contextualReference.classification,
      matched: contextualReference.classification,
    };
  }
  if (contextualReference.subject === "third_person") {
    return {
      level: contextualReference.classification as "third_person_current" | "third_person_historical",
      safetySignal: false,
      ambiguousSafetySignal: false,
      realHelpSignal: false,
      urgent: Boolean(contextualReference.otherPersonCurrentConcern),
      reviewFlagRecommended: false,
      safetyContextClassification: contextualReference.classification,
      otherPersonSafetySignal: Boolean(contextualReference.otherPersonCurrentConcern),
      matched: contextualReference.classification,
    };
  }
  if (contextualReference.classification === "quoted_or_informational") {
    return {
      level: "quoted_or_informational" as const,
      safetySignal: false,
      ambiguousSafetySignal: false,
      realHelpSignal: false,
      urgent: false,
      reviewFlagRecommended: false,
      safetyContextClassification: contextualReference.classification,
      quotedOrInformationalSafetyReference: true,
      matched: contextualReference.classification,
    };
  }
  if (contextualReference.classification === "first_person_historical") {
    return {
      level: "first_person_historical" as const,
      safetySignal: false,
      ambiguousSafetySignal: true,
      realHelpSignal: false,
      urgent: false,
      reviewFlagRecommended: false,
      safetyContextClassification: contextualReference.classification,
      historicalSafetySignal: true,
      matched: contextualReference.classification,
    };
  }
  if (
    contextualReference.requiresClarification &&
    contextualReference.subject === "ambiguous" &&
    !/\b(no hope|nothing left|barely holding on|no reason to live|life isn'?t worth living)\b/i.test(normalized)
  ) {
    return {
      level: "ambiguous_safety_reference" as const,
      safetySignal: false,
      ambiguousSafetySignal: true,
      realHelpSignal: false,
      urgent: false,
      reviewFlagRecommended: false,
      safetyContextClassification: contextualReference.classification,
      matched: contextualReference.classification,
    };
  }

  const boundedHoldingSignal = detectBoundedHoldingSafetySignal(messages, normalized);
  if (boundedHoldingSignal) return boundedHoldingSignal;

  const directDanger = [
    /\b(i('| a)?m going to|i want to|i might|i could|i feel like|i plan to)\b.{0,40}\b(kill myself|end my life|die|self[-\s]?harm|harm myself|hurt myself)\b/i,
    /\b(kill myself|end my life|take my own life|suicidal|suicide|self[-\s]?harm|harm myself|hurt myself)\b/i,
    /\b(i('| a)?m not safe|i am not safe|unsafe right now|in immediate danger)\b/i,
  ];
  const ambiguousSafety = [
    /\b(i don'?t know if i can keep going|i do not know if i can keep going)\b/i,
    /\b(i can'?t keep going|i cannot keep going|can'?t keep going|cannot keep going)\b/i,
    /\b(i can'?t do this anymore|i cannot do this anymore|i'?m done|i am done|i'?m at breaking point|i am at breaking point)\b/i,
  ];
  const passiveRisk = [
    /\b(everyone would be better off without me|better off without me)\b/i,
    /\b(i can'?t go on|i cannot go on|no reason to live|wish i (?:wasn'?t|were not) here)\b/i,
    /\b(i want to disappear|what'?s the point of living|life isn'?t worth living)\b/i,
  ];
  const realHelp = [
    /\b(i need real help|this isn'?t enough|this is not enough|i need someone to actually help me)\b/i,
    /\b(i don'?t know who to talk to|i do not know who to talk to|i can'?t do this alone|i cannot do this alone)\b/i,
    /\b(not just breathing exercises|need more than (?:coaching|breathing|this))\b/i,
  ];

  const directMatch = directDanger.map((pattern) => normalized.match(pattern)?.[0]).find(Boolean);
  if (directMatch) {
    return {
      level: "direct_danger" as const,
      safetySignal: true,
      ambiguousSafetySignal: false,
      realHelpSignal: false,
      urgent: true,
      reviewFlagRecommended: true,
      matched: normalizeText(directMatch),
    };
  }

  const passiveMatch = passiveRisk.map((pattern) => normalized.match(pattern)?.[0]).find(Boolean);
  if (passiveMatch) {
    return {
      level: "passive_risk" as const,
      safetySignal: true,
      ambiguousSafetySignal: false,
      realHelpSignal: false,
      urgent: true,
      reviewFlagRecommended: true,
      matched: normalizeText(passiveMatch),
    };
  }

  const ambiguousMatch = ambiguousSafety.map((pattern) => normalized.match(pattern)?.[0]).find(Boolean);
  if (ambiguousMatch) {
    return {
      level: "ambiguous_safety" as const,
      safetySignal: false,
      ambiguousSafetySignal: true,
      realHelpSignal: false,
      urgent: false,
      reviewFlagRecommended: false,
      matched: normalizeText(ambiguousMatch),
    };
  }

  const realHelpMatch = realHelp.map((pattern) => normalized.match(pattern)?.[0]).find(Boolean);
  if (realHelpMatch) {
    return {
      level: "real_help" as const,
      safetySignal: false,
      ambiguousSafetySignal: false,
      realHelpSignal: true,
      urgent: false,
      reviewFlagRecommended: false,
      matched: normalizeText(realHelpMatch),
    };
  }

  const contextualState = getSafetyContextState(messages);
  const currentSafetyWindow = getSafetyContextWindow(messages, contextualState);
  const cumulativeSignal = detectCumulativeSafetyAdjacentSignal(currentSafetyWindow, normalized);
  if (cumulativeSignal) {
    return {
      level: "cumulative_safety_adjacent" as const,
      safetySignal: false,
      ambiguousSafetySignal: false,
      cumulativeSafetyAdjacentSignal: true,
      realHelpSignal: false,
      urgent: false,
      reviewFlagRecommended: true,
      matched: normalizeText(cumulativeSignal.matched || "cumulative_safety_adjacent_distress"),
    };
  }

  return {
    level: "none" as const,
    safetySignal: false,
    ambiguousSafetySignal: false,
    realHelpSignal: false,
    urgent: false,
    reviewFlagRecommended: false,
    matched: "",
  };
}

function coerceMessages(input: CoachingStateInput): MessageLike[] {
  if (Array.isArray(input.messages) && input.messages.length) return input.messages;
  const messages: MessageLike[] = [];
  if (input.latestUserMessage) messages.push({ role: "user", content: input.latestUserMessage });
  (input.recentAssistantMessages || []).forEach((text) => {
    if (normalizeText(text)) messages.push({ role: "assistant", content: text });
  });
  return messages;
}

function latestUserText(messages: MessageLike[], fallback = ""): string {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const entry = messages[index];
    const role = String(entry?.role || entry?.sender || "").toLowerCase();
    if (role === "user") {
      return normalizeText(entry?.content || entry?.text || "");
    }
  }
  return normalizeText(fallback);
}

function userTexts(messages: MessageLike[] = []): string[] {
  return messages
    .filter((entry) => String(entry?.role || entry?.sender || "").toLowerCase() === "user")
    .map((entry) => normalizeText(entry?.content || entry?.text || ""))
    .filter(Boolean);
}

function assistantTexts(messages: MessageLike[], fallback: string[] = []): string[] {
  const fromMessages = messages
    .filter((entry) => ["assistant", "nia"].includes(String(entry?.role || entry?.sender || "").toLowerCase()))
    .map((entry) => normalizeText(entry?.content || entry?.text || ""))
    .filter(Boolean);
  return fromMessages.length ? fromMessages : fallback.map((item) => normalizeText(item)).filter(Boolean);
}

function pickPresentingIssue(text: string, summary: Record<string, unknown> | null): string {
  return normalizeText(text || String(summary?.summaryText || "")) || "General support need";
}

function extractSentences(text: string): string[] {
  return normalizeText(text)
    .split(/(?<=[.!?])\s+/)
    .map((part) => normalizeText(part))
    .filter(Boolean);
}

function extractActions(text: string): string[] {
  const participantOwnedText = maskQuotedParticipantSpeech(text);
  const canonicalAgreedAction = participantOwnedText.match(
    /\bmy agreed action (?:is|remains)(?: to)?\s+([^.!?]*)/i
  );
  const matches = participantOwnedText.match(/\b(my agreed action (?:is|remains)(?: to)?|i will|i'll|i'm going to|i am going to|i plan to|i decided(?: to)?|i have decided to|i've decided to|my plan is(?: to)?|my decision is(?: to)?|my next step is|what i'm going to do is(?: to)?)\b[^.!?]*/gi) || [];
  const completed = participantOwnedText.match(/\b(i have|i've)\s+(written|completed|finished|sent|called|booked|scheduled|started|done)\b[^.!?]*/gi) || [];
  const canonical = canonicalAgreedAction?.[1]
    ? `My agreed action is to ${normalizeText(canonicalAgreedAction[1]).replace(/[.]+$/g, "")}`
    : "";
  return Array.from(new Set([canonical, ...matches, ...completed].map((item) => normalizeText(item)).filter(Boolean))).slice(0, 3);
}

function hasActionCommitmentSignal(text: string): boolean {
  return /\b(my agreed action is(?: to)?|i will|i'll|i am going to|i'm going to|i plan to|i decided(?: to)?|i have decided to|i've decided to|my plan is(?: to)?|my decision is(?: to)?|my next step is|what i'm going to do is(?: to)?|i'll do that|i will do that|i'm going to do that|i am going to do that|i have (?:written|completed|finished|sent|called|booked|scheduled|started|done)|i've (?:written|completed|finished|sent|called|booked|scheduled|started|done))\b/i.test(
    maskQuotedParticipantSpeech(text)
  );
}

function inferCompletionReason(text: string): string {
  if (/\b(thank you|thanks)\b/i.test(text)) return "gratitude";
  if (/\b(that helps|this helps|helpful|that was helpful|this was helpful)\b/i.test(text)) return "helpful";
  if (/\b(i feel better|i feel clearer|i feel more clear|i feel calmer|i feel more grounded)\b/i.test(text)) return "shift";
  if (isUnambiguousExplicitSessionClose(text)) return "explicit_close";
  if (hasActionCommitmentSignal(text)) return "action_commitment";
  return "";
}

function detectNaturalCompletion(text: string, actions: string[]): { complete: boolean; reason: string; action: string } {
  const normalized = normalizeText(text);
  if (!normalized) return { complete: false, reason: "", action: "" };
  const explicitSessionClose = isUnambiguousExplicitSessionClose(normalized);
  if (/\?/.test(normalized) && !explicitSessionClose) return { complete: false, reason: "", action: "" };
  if (/\b(but|however|still|though|yet)\b/i.test(normalized)) return { complete: false, reason: "", action: "" };
  if (/\b(thanks|thank you),?\s+i guess\b/i.test(normalized)) return { complete: false, reason: "", action: "" };

  const explicitClose = explicitSessionClose || /\b(thank you|thanks|that helps|this helps|that was helpful|this was helpful|i will do that|i'll do that|i feel better|i feel clearer|i feel more clear|i feel calmer|i feel more grounded|we can leave it there|leave it there|that'?s enough for now|enough for now|done for now|we can stop here|let'?s stop there|(?:this|the) session (?:is|feels) complete(?:d)?)\b/i.test(normalized);
  const gratitudeOrResolution = /\b(thank you|thanks|that helps|this helps|helpful|that was helpful|this was helpful|i feel better|i feel clearer|i feel more clear|i feel calmer|i feel more grounded)\b/i.test(normalized);
  const actionCommitment = hasActionCommitmentSignal(normalized) || actions.length > 0;
  const complete = explicitClose || (gratitudeOrResolution && actionCommitment);

  return {
    complete,
    reason: complete ? inferCompletionReason(normalized) : "",
    action: actions[0] || "",
  };
}

function extractTimelines(text: string): string[] {
  const matches = text.match(/\b(today|tomorrow|this week|next week|by [a-z]+|by \d{1,2}(:\d{2})?|on monday|on tuesday|on wednesday|on thursday|on friday)\b/gi) || [];
  return Array.from(new Set(matches.map((item) => normalizeText(item)))).slice(0, 3);
}

function extractFutureState(text: string): string {
  const match = text.match(/\b(i want to|i need to|i'd like to|i would like to)\b[^.!?]*/i);
  return normalizeText(match?.[0] || "") || "A clearer, more workable next state";
}

function extractImmediateTarget(text: string, actions: string[]): string {
  return actions[0] || extractFutureState(text) || "Clarify the next concrete move";
}

function extractSupportNeeds(text: string): string[] {
  const results: string[] = [];
  if (/\b(help|support|guidance)\b/i.test(text)) results.push("Guidance");
  if (/\b(accountability|check in|follow up|remind me)\b/i.test(text)) results.push("Accountability");
  if (/\b(space|time|focus|quiet)\b/i.test(text)) results.push("Protected environment");
  return Array.from(new Set(results)).slice(0, 3);
}

function extractRepeatPhrases(text: string, blocks: DetectedBlocksResult): string[] {
  const blockEligibleText = stripLogisticalAvailabilityStatements(text);
  const phrases = [
    ...blocks.matches.map((entry) => entry.matchedText),
    ...(blockEligibleText.match(/\b(i can'?t|i cannot|have to|need to|always|never|what if|why bother)\b/gi) || []),
  ].map((item) => normalizeText(item)).filter(Boolean);
  return Array.from(new Set(phrases)).slice(0, 5);
}

function splitObstacles(text: string, blocks: DetectedBlocksResult): { internal: string[]; external: string[]; beliefs: string[]; distortions: string[] } {
  const internal = Array.from(new Set([
    ...blocks.egoVictimPatterns.map((entry) => entry.label),
    ...blocks.stoppingQuestions.map((entry) => entry.label),
    ...blocks.disqualifiers.map((entry) => entry.label),
  ])).slice(0, 5);
  const externalMatches = text.match(/\b(deadline|meetings|workload|boss|team|noise|time|money|resources|calendar|interruptions)\b/gi) || [];
  const external = Array.from(new Set(externalMatches.map((item) => normalizeText(item)))).slice(0, 5);
  const beliefs = Array.from(new Set([
    ...blocks.disqualifiers.map((entry) => entry.matchedText),
    ...blocks.metaModelSignals.filter((entry) => entry.key === "modal_impossibility").map((entry) => entry.matchedText),
  ])).slice(0, 5);
  const distortions = Array.from(new Set([
    ...blocks.metaModelSignals.map((entry) => entry.label),
    ...blocks.disqualifiers.filter((entry) => entry.key === "all_or_nothing").map((entry) => entry.label),
  ])).slice(0, 5);
  return { internal, external, beliefs, distortions };
}

function hasAvoidanceSignal(text: string): boolean {
  return /\b(putting it off|put it off|keep putting it off|procrastinat|avoid|back off|can'?t get started|cannot get started|can'?t start|cannot start)\b/i.test(text);
}

function hasDisqualifierSignal(text: string): boolean {
  return /\b(it won'?t work|it will not work|i can'?t|i cannot|i('?m| am) not good enough|this is just who i am)\b/i.test(
    stripLogisticalAvailabilityStatements(text)
  );
}

function hasPredictedOutcomeSignal(text: string): boolean {
  return /\b(will kick in|inevitably|as soon as|probably won'?t work|probably will not work|it will happen that way|i('?| wi)ll get delayed|i will get delayed|lose momentum|it('?| wi)ll fall apart|it will fall apart)\b/i.test(text);
}

function hasDistressOrLowEnergySignal(text: string): boolean {
  return /\b(overwhelmed|panic|panicked|anxious|anxiety|stressed|stress|shut down|shutdown|exhausted|drained|burnt out|burned out|fatigued|fatigue|tired|low energy|flat|blank|numb|foggy)\b/i.test(text);
}

function hasConfusionOrSuggestionSignal(text: string): boolean {
  return /\b(i do(n't| not) know what to do|not sure what to do|unsure what to do|what should i do|what do i do|any suggestions|can you suggest|give me a suggestion|help me decide)\b/i.test(text);
}

function hasOpenEmotionalDisclosure(text: string): boolean {
  return /\b(i feel|i felt|i'?m feeling|i am feeling|it feels|this feels|i've been feeling|i have been feeling)\b/i.test(text);
}

function hasGriefOrLossSignal(text: string): boolean {
  return /\b(sad|sadness|grief|grieving|loss|bereavement|bereaved|died|passed away|miss them|miss him|miss her|miss it|heartbroken|mourning)\b/i.test(text);
}

function isOnboardingHelpRequest(text: string): boolean {
  return /\b(what can you help me with|how can you help me|what do you help with|what support do you offer|what can nia help with|how does this system work|what is neurogres|what is nia coach)\b/i.test(text);
}

function hasInformationalIntent(text: string): boolean {
  return /\b(what('?s| is) the difference between|difference between|compare|comparison|what is|what are|who is|define|definition|meaning of|explain|how does|how do(?:es)? this system work|tell me about)\b/i.test(text);
}

function hasAdvisoryIntent(text: string): boolean {
  return /\b(guide me|simple steps|step by step|steps\/process|steps or process|process|framework|approach|how should (i|we)|best way to|plan for|strategy for|what are the steps|give me a process)\b/i.test(text);
}

function hasStrongCoachingSignal(text: string): boolean {
  return hasDistressOrLowEnergySignal(text)
    || hasOpenEmotionalDisclosure(text)
    || hasGriefOrLossSignal(text)
    || hasAvoidanceSignal(text)
    || hasDisqualifierSignal(text)
    || hasPredictedOutcomeSignal(text)
    || hasContradictionSignal(text)
    || /\b(i need help with me|help me get unstuck|i feel stuck|i keep stopping|i keep shutting down|i'm overwhelmed|i am overwhelmed|i need coaching)\b/i.test(text);
}

function inferResponseRoute(params: {
  latestUserMessage: string;
  persistentObstacle: boolean;
  symptomLikeReply: boolean;
}): "inform" | "advise" | "coach" {
  const { latestUserMessage, persistentObstacle, symptomLikeReply } = params;
  const text = normalizeText(latestUserMessage);
  if (!text) return "coach";

  if (persistentObstacle || symptomLikeReply || hasStrongCoachingSignal(text)) {
    return "coach";
  }

  if (isOnboardingHelpRequest(text)) {
    return "inform";
  }

  const informationalIntent = hasInformationalIntent(text);
  const advisoryIntent = hasAdvisoryIntent(text);

  if (informationalIntent && !advisoryIntent) return "inform";
  if (advisoryIntent) return "advise";
  if (/\?$/.test(text) && !/\b(i feel|i'm feeling|i am feeling|overwhelmed|stuck|can't|cannot|won't work|not good enough)\b/i.test(text)) {
    return "inform";
  }

  return "coach";
}

function hasContradictionSignal(text: string): boolean {
  const normalized = normalizeText(text).toLowerCase();
  if (!normalized) return false;
  const hasIntention = /\b(i want to|i need to|i plan to|i should|i know i should|i mean to)\b/.test(normalized);
  const hasContrast = /\b(but|however|yet|still|instead)\b/.test(normalized);
  const hasStopping = /\b(stop|stop myself|don't start|do not start|never do|don't do it|do not do it|quit|freeze|avoid)\b/.test(normalized);
  return (hasIntention && hasContrast && hasStopping) || /\b(i want to start[,]? but\b|i try to start[,]? but\b|i stop myself\b)\b/.test(normalized);
}

function inferCoachingMode(params: {
  latestUserMessage: string;
  allUserTexts: string[];
  currentStepId: string;
  persistentObstacle: boolean;
  forceObstacleFirst: boolean;
  predictedOutcomeStory: boolean;
  symptomLikeReply: boolean;
}): "explore" | "direct" | "challenge" | "stabilise" {
  const {
    latestUserMessage,
    allUserTexts,
    currentStepId,
    persistentObstacle,
    forceObstacleFirst,
    predictedOutcomeStory,
    symptomLikeReply,
  } = params;
  const earlyTurn = allUserTexts.length <= 2;
  const distress = hasDistressOrLowEnergySignal(latestUserMessage) || symptomLikeReply;
  const confusion = hasConfusionOrSuggestionSignal(latestUserMessage);
  const griefOrLoss = hasGriefOrLossSignal(latestUserMessage);
  const contradiction = hasContradictionSignal(latestUserMessage);
  const forceChallenge = contradiction;
  const avoidanceChallenge = forceObstacleFirst || persistentObstacle || (hasAvoidanceSignal(latestUserMessage) && (hasDisqualifierSignal(latestUserMessage) || contradiction));
  const practicalDirection = /\b(plan|schedule|decide|choose|next step|priority|prioritise|prioritize|suggest|advice)\b/i.test(latestUserMessage);
  const emotionalDisclosure = hasOpenEmotionalDisclosure(latestUserMessage);

  if (distress) return "stabilise";
  if (griefOrLoss && !confusion && !practicalDirection) return "explore";
  if (forceChallenge || avoidanceChallenge || predictedOutcomeStory) return "challenge";
  if (confusion || practicalDirection || currentStepId === "commit" || currentStepId === "anchor") return "direct";
  if (earlyTurn && (emotionalDisclosure || currentStepId === "environment" || currentStepId === "clarify")) return "explore";
  return "direct";
}

function isShortSymptomReply(text: string): boolean {
  const normalized = normalizeText(text);
  if (!normalized) return false;
  const words = normalized.split(/\s+/).filter(Boolean);
  if (words.length > 6) return false;
  return /\b(fatigued|fatigue|tired|mostly tired|exhausted|shut down|shutdown|not sure|unsure|blank|stuck|flat|i guess|just tired|just fatigued)\b/i.test(normalized);
}

function clearlyChangesTopic(text: string): boolean {
  return /\b(by the way|different topic|another thing|separately|instead i want to talk about|about my partner|about my family|at work today)\b/i.test(text);
}

function findPersistableObstacle(previousUserTexts: string[]): { text: string; blocks: DetectedBlocksResult } | null {
  for (let index = previousUserTexts.length - 1; index >= 0; index -= 1) {
    const text = previousUserTexts[index];
    const blocks = detectBlocks(text);
    if ((hasAvoidanceSignal(text) && hasDisqualifierSignal(text)) || blocks.primaryBlock) {
      return { text, blocks };
    }
  }
  return null;
}

function inferObstacleQuestion(text: string, blocks: DetectedBlocksResult): string {
  if (hasAvoidanceSignal(text) && hasDisqualifierSignal(text)) {
    return "What do you tell yourself in the exact moment you stop yourself from starting?";
  }
  if (blocks.stoppingQuestions[0]?.matchedText) {
    return `What happens in the exact moment ${normalizeText(blocks.stoppingQuestions[0].matchedText).toLowerCase()} starts running the show?`;
  }
  return "What happens in the exact moment you stop yourself from starting?";
}

function inferPredictedOutcomeQuestion(text: string): string {
  if (/\b(as soon as|will kick in)\b/i.test(text)) {
    return "What comes first there in your mind: the starting point, or the prediction that the fatigue will kick in?";
  }
  if (/\b(inevitably|probably won'?t work|probably will not work)\b/i.test(text)) {
    return "How certain does that prediction feel in the moment, and what tells you it will happen that way?";
  }
  if (/\b(i('?| wi)ll get delayed|i will get delayed|lose momentum)\b/i.test(text)) {
    return "What do you assume that delay or loss of momentum means when you picture it happening?";
  }
  return "What tells you it will happen that way?";
}

export function buildCoachingState(input: CoachingStateInput = {}): CoachingState {
  const messages = coerceMessages(input);
  const allUserTexts = userTexts(messages);
  const latestUserMessage = latestUserText(messages, input.latestUserMessage || "");
  const operationalIntent = classifyOperationalIntent(messages.length ? messages : latestUserMessage);
  const factualPlanning = classifyFactualPlanningIntent(messages.length ? messages : latestUserMessage);
  const neutralNonCoachingIntent = operationalIntent.operational || factualPlanning.factualPlanning;
  const logisticalAvailabilityUpdate = isLogisticalAvailabilityStatement(latestUserMessage);
  const blockEligibleLatestMessage = stripLogisticalAvailabilityStatements(
    operationalIntent.operational ? operationalIntent.residualText : latestUserMessage
  );
  const previousUserTexts = allUserTexts.slice(0, -1);
  const recentAssistantMessages = assistantTexts(messages, input.recentAssistantMessages || []);
  const currentStep = inferCurrentStep([
    { role: "user", content: latestUserMessage },
    ...recentAssistantMessages.slice(-2).map((content) => ({ role: "assistant", content })),
  ]);
  const presentingIssue = pickPresentingIssue(latestUserMessage, input.sessionSummary || null);
  const symptomLikeReply = isShortSymptomReply(latestUserMessage);
  const priorObstacle = !neutralNonCoachingIntent && !clearlyChangesTopic(latestUserMessage)
    ? findPersistableObstacle(previousUserTexts)
    : null;
  const persistentObstacle = Boolean(!neutralNonCoachingIntent && priorObstacle && symptomLikeReply);
  const predictedOutcomeStory = !neutralNonCoachingIntent
    && (hasPredictedOutcomeSignal(latestUserMessage) || (persistentObstacle && hasPredictedOutcomeSignal(priorObstacle?.text || "")));
  const summaryBlockEvidence = logisticalAvailabilityUpdate || neutralNonCoachingIntent
    ? ""
    : String(input.sessionSummary?.summaryText || "");
  const effectiveObstacleText = persistentObstacle
    ? [priorObstacle?.text || "", blockEligibleLatestMessage, summaryBlockEvidence].filter(Boolean).join(" ")
    : [blockEligibleLatestMessage, summaryBlockEvidence].filter(Boolean).join(" ");
  const currentStory = persistentObstacle
    ? extractSentences(priorObstacle?.text || "")[0] || extractSentences(latestUserMessage)[0] || presentingIssue
    : extractSentences(latestUserMessage)[0] || presentingIssue;
  const detectedBlocks = detectBlocks(effectiveObstacleText);
  const targetSkills = inferSkillsFromBlocks({
    detectedBlocks,
    summaryText: summaryBlockEvidence,
  });
  const allowGames = !neutralNonCoachingIntent && !["environment", "neuro_link"].includes(currentStep.id);
  const gamePlan = allowGames ? buildGamePlan(targetSkills) : { recommendedGames: [], rationale: [] };
  const actions = extractActions(latestUserMessage);
  const completion = detectNaturalCompletion(latestUserMessage, actions);
  const timelines = extractTimelines(latestUserMessage);
  const futureState = extractFutureState(latestUserMessage);
  const immediateTarget = extractImmediateTarget(latestUserMessage, actions);
  const supportNeeds = extractSupportNeeds(latestUserMessage);
  const safetyEscalation = detectSafetyEscalationSignal(latestUserMessage, messages);
  const obstacles = neutralNonCoachingIntent
    ? { internal: [], external: [], beliefs: [], distortions: [] }
    : splitObstacles(blockEligibleLatestMessage, detectedBlocks);
  const forceObstacleFirst = !neutralNonCoachingIntent
    && hasAvoidanceSignal(latestUserMessage)
    && hasDisqualifierSignal(latestUserMessage);
  const forceChallenge = !neutralNonCoachingIntent && hasContradictionSignal(latestUserMessage);
  const obstacleFirst = forceObstacleFirst || persistentObstacle;
  const predictedOutcomeQuestion = inferPredictedOutcomeQuestion(persistentObstacle ? (priorObstacle?.text || latestUserMessage) : latestUserMessage);
  const obstacleQuestion = persistentObstacle
    ? "What happens first there: the fatigue, or the thought that it probably will not work?"
    : inferObstacleQuestion(latestUserMessage, detectedBlocks);
  const promptPriority = forceObstacleFirst
    ? "force_obstacle_first"
    : obstacleFirst
      ? "obstacle_first"
      : predictedOutcomeStory
        ? "predicted_outcome"
        : "default";
  const followUpPlan = buildFollowUpPlan({
    currentGoal: futureState || immediateTarget,
    actions,
    recommendedGames: gamePlan.recommendedGames,
    currentStep,
    detectedRisks: detectedBlocks.matchedBlocks,
    resistanceFactors: obstacles.internal,
  });
  const inferredResponseRoute = inferResponseRoute({
    latestUserMessage,
    persistentObstacle,
    symptomLikeReply,
  });
  const cumulativeSafetyAdjacent = Boolean(
    (safetyEscalation as { cumulativeSafetyAdjacentSignal?: boolean }).cumulativeSafetyAdjacentSignal
  );
  const hasSafetyOverride = safetyEscalation.safetySignal
    || safetyEscalation.ambiguousSafetySignal
    || cumulativeSafetyAdjacent
    || safetyEscalation.realHelpSignal;
  const responseRoute: CoachingState["responseRoute"] = hasSafetyOverride
    ? "coach"
    : operationalIntent.operational
      ? "support"
      : factualPlanning.factualPlanning
        ? "inform"
        : inferredResponseRoute;
  const coachingMode = hasSafetyOverride
    ? "stabilise"
    : neutralNonCoachingIntent
      ? "direct"
      : inferCoachingMode({
          latestUserMessage,
          allUserTexts,
          currentStepId: currentStep.id,
          persistentObstacle,
          forceObstacleFirst,
          predictedOutcomeStory,
          symptomLikeReply,
        });

  const state: CoachingState = {
    responseRoute,
    currentStep,
    promptPriority,
    coachingMode,
    safetyEscalation,
    forceChallenge,
    presentingIssue,
    currentStory,
    repeatPhrases: neutralNonCoachingIntent ? [] : extractRepeatPhrases(latestUserMessage, detectedBlocks),
    obstacleFirst,
    forceObstacleFirst,
    persistentObstacle,
    symptomLikeReply,
    predictedOutcomeStory,
    predictedOutcomeQuestion: neutralNonCoachingIntent ? "" : predictedOutcomeQuestion,
    obstacleQuestion: neutralNonCoachingIntent ? "" : obstacleQuestion,
    primaryStoppingQuestion: neutralNonCoachingIntent ? "" : detectedBlocks.stoppingQuestions[0]?.matchedText || "",
    primaryBlock: neutralNonCoachingIntent ? "" : detectedBlocks.primaryBlock?.label || "",
    internalObstacles: obstacles.internal,
    externalObstacles: obstacles.external,
    limitingBeliefs: obstacles.beliefs,
    distortionPatterns: obstacles.distortions,
    targetSkills: neutralNonCoachingIntent ? [] : targetSkills,
    recommendedGames: neutralNonCoachingIntent ? [] : gamePlan.recommendedGames,
    gameRationales: neutralNonCoachingIntent ? [] : gamePlan.rationale,
    futureState: neutralNonCoachingIntent ? "" : futureState,
    immediateTarget: neutralNonCoachingIntent ? "" : immediateTarget,
    actions: neutralNonCoachingIntent ? [] : actions,
    timelines: neutralNonCoachingIntent ? [] : timelines,
    supportNeeds: neutralNonCoachingIntent ? [] : supportNeeds,
    nextFollowUpFocus: neutralNonCoachingIntent ? "" : followUpPlan.nextFollowUpFocus,
    sessionComplete: neutralNonCoachingIntent ? false : completion.complete,
    completionReason: neutralNonCoachingIntent ? "" : completion.reason,
    completionAction: neutralNonCoachingIntent ? "" : completion.action,
    operationalIntent: {
      operational: Boolean(operationalIntent.operational),
      complaint: Boolean(operationalIntent.complaint),
      escalationRequest: Boolean(operationalIntent.escalationRequest),
      missingSummary: Boolean(operationalIntent.missingSummary),
      continuityRequest: Boolean(operationalIntent.continuityRequest),
      recordRequest: Boolean(operationalIntent.recordRequest),
      reminderComplaint: Boolean(operationalIntent.reminderComplaint),
      deliveryComplaint: Boolean(operationalIntent.deliveryComplaint),
      inaccurateRecap: Boolean(operationalIntent.inaccurateRecap),
      establishedContext: Boolean(operationalIntent.establishedContext),
      pureOperational: Boolean(operationalIntent.pureOperational),
      hasCoachingTopic: Boolean(operationalIntent.hasCoachingTopic),
      residualText: normalizeText(operationalIntent.residualText || ""),
      reason: normalizeText(operationalIntent.reason || ""),
    },
    factualPlanning: {
      factualPlanning: Boolean(factualPlanning.factualPlanning),
      retirementPlanning: Boolean(factualPlanning.retirementPlanning),
    },
  };

  if (process.env.NODE_ENV !== "production") {
    state.debugStep = currentStep.id;
    state.debugSkills = neutralNonCoachingIntent ? [] : [...targetSkills];
    state.debugGames = neutralNonCoachingIntent ? [] : [...gamePlan.recommendedGames];
  }

  return state;
}
